// Path: src/components/common/LogoutButton.tsx
// Description: Common logout button component with user dropdown menu

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LogOut, User, Key, X, Lock, ChevronDown, ScanFace } from 'lucide-react';
import { changePassword } from '../../lib/api/users';
import FaceRegisterModal from './FaceRegisterModal';

const LogoutButton: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showFaceRegister, setShowFaceRegister] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changePasswordError, setChangePasswordError] = useState('');
  const [changePasswordSuccess, setChangePasswordSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const validatePasswordStrength = (password: string): string | null => {
    if (password.length < 8) {
      return '密码长度必须至少8位';
    }
    
    const hasUpperCase = /[A-Z]/.test(password);
    if (!hasUpperCase) {
      return '密码必须包含至少1个大写字母';
    }
    
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
    if (!hasSpecialChar) {
      return '密码必须包含至少1个特殊字符';
    }
    
    const hasLetter = /[a-zA-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    
    if (!hasLetter || !hasNumber || !hasSpecialChar) {
      return '密码必须同时包含字母、数字和特殊字符';
    }
    
    return null;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setChangePasswordError('');
    setChangePasswordSuccess('');

    if (!oldPassword.trim() || !newPassword.trim() || !confirmPassword.trim()) {
      setChangePasswordError('请填写所有字段');
      return;
    }

    if (newPassword !== confirmPassword) {
      setChangePasswordError('两次输入的新密码不一致');
      return;
    }

    const passwordError = validatePasswordStrength(newPassword);
    if (passwordError) {
      setChangePasswordError(passwordError);
      return;
    }

    setIsChangingPassword(true);
    try {
      await changePassword({ old_password: oldPassword, new_password: newPassword });
      setChangePasswordSuccess('密码修改成功');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => {
        setShowChangePassword(false);
        setChangePasswordSuccess('');
        setShowDropdown(false);
      }, 1500);
    } catch (err: any) {
      setChangePasswordError(err.response?.data?.detail || '密码修改失败');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const closeChangePasswordModal = () => {
    setShowChangePassword(false);
    setChangePasswordError('');
    setChangePasswordSuccess('');
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  return (
    <div className="flex items-center gap-3 whitespace-nowrap" ref={dropdownRef}>
      {user && (
        <div className="relative">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="flex items-center gap-2 text-sm whitespace-nowrap px-2 py-1 rounded-lg hover:bg-white/5 transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
              <User size={16} className="text-blue-400" />
            </div>
            <span className="font-medium">{user.username}</span>
            <span className="text-xs text-gray-500 bg-white/5 px-2 py-0.5 rounded-full">
              {user.role}
            </span>
            {user.memo && (
              <span className="text-xs text-blue-500 bg-blue-500/10 px-2 py-0.5 rounded-full">
                {user.memo}
              </span>
            )}
            <ChevronDown size={14} className={`text-gray-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
          </button>

          {showDropdown && (
            <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
              <button
                onClick={() => {
                  setShowFaceRegister(true);
                  setShowDropdown(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <ScanFace size={16} />
                录入人脸
              </button>
              <button
                onClick={() => {
                  setShowChangePassword(true);
                  setShowDropdown(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <Key size={16} />
                修改密码
              </button>
            </div>
          )}
        </div>
      )}
      
      <button
        onClick={handleLogout}
        className="flex items-center justify-center p-2 rounded-lg bg-white/5 hover:bg-red-500/10 text-gray-600 hover:text-red-600 border border-white/10 hover:border-red-500/30 transition-all duration-200"
        title="退出登录"
      >
        <LogOut size={18} />
      </button>

      {showChangePassword && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="relative w-[320px] bg-white rounded-xl p-6 shadow-2xl">
            <button
              type="button"
              onClick={closeChangePasswordModal}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={18} />
            </button>
            
            <h3 className="text-lg font-semibold text-gray-900 mb-4">修改密码</h3>
            
            <form onSubmit={handleChangePassword} className="space-y-3">
              {changePasswordError && (
                <div className="bg-red-50 text-red-600 px-3 py-2 rounded text-xs text-center">
                  {changePasswordError}
                </div>
              )}
              {changePasswordSuccess && (
                <div className="bg-green-50 text-green-600 px-3 py-2 rounded text-xs text-center">
                  {changePasswordSuccess}
                </div>
              )}
              
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-600">
                <p className="font-medium mb-1">密码要求：</p>
                <ul className="space-y-0.5 text-gray-500">
                  <li>• 长度至少8位</li>
                  <li>• 包含至少1个大写字母</li>
                  <li>• 包含至少1个特殊字符</li>
                  <li>• 同时包含字母、数字和特殊字符</li>
                </ul>
              </div>
              
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock size={16} className="text-gray-400" />
                </div>
                <input
                  type="password"
                  placeholder="原密码"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  className="block w-full pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  disabled={isChangingPassword}
                />
              </div>
              
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock size={16} className="text-gray-400" />
                </div>
                <input
                  type="password"
                  placeholder="新密码"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="block w-full pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  disabled={isChangingPassword}
                />
              </div>
              
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock size={16} className="text-gray-400" />
                </div>
                <input
                  type="password"
                  placeholder="确认新密码"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="block w-full pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  disabled={isChangingPassword}
                />
              </div>
              
              <div className="pt-2">
                <button
                  type="submit"
                  className="w-full flex justify-center py-2.5 px-4 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all disabled:opacity-50"
                  disabled={isChangingPassword}
                >
                  {isChangingPassword ? '处理中...' : '确认修改'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      <FaceRegisterModal 
        isOpen={showFaceRegister}
        onClose={() => setShowFaceRegister(false)}
      />
    </div>
  );
};

export default LogoutButton;
